import { set } from "@ember/object"
import Evented from "@ember/object/evented"
import { begin, end } from "@ember/runloop"
import Service, { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"

import type { ActionDoc } from "@shared/models/action"
import type { PatchModel } from "@shared/models/patch"
import type { TypeToModel } from "@shared/models/type-to-model"
import { Queue } from "cc-frontend/utils/queue"
import type InternalEmberStore from "./internal-ember-store"
import type InternalSupergrainStore from "./internal-supergrain-store"
import type Session from "./session"

interface OptimisticPatchData {
  patch: PatchModel
  rewind: () => void
}

type ServerPatchResourceType = "patch" | "patch-summary"

interface ServerPatchDocument {
  id: string
  modelType: string
  ops?: PatchModel["document"]["ops"]
}

interface ServerPatchAttributes {
  document: ServerPatchDocument
  embeddedDocument?: PatchModel["embeddedDocument"]
  actionId?: string | null
  timeAtCreation?: string | null
  revisionAtCreation?: string | number | null
  revisionAfterServerApplication?: number
  versionAfterServerApplication?: number | null
  isCreatePatch?: boolean
}

interface ServerPatchResource {
  id: string
  type: ServerPatchResourceType
  attributes: ServerPatchAttributes
}

interface ServerPatchModel extends PatchModel {
  id: string
  revisionAfterServerApplication?: number
  versionAfterServerApplication?: number | null
}

export type ServerPatchReceiveResult =
  | { kind: "accepted"; patch: PatchModel | null }
  | { kind: "ignored" }
  | { kind: "request-revision"; modelType: string; modelId: string; revision: number }

export default class PatchCoordinator extends Service.extend(Evented) {
  @service("internal-ember-store") declare internalEmberStore: InternalEmberStore
  @service declare session: Session
  @service("internal-supergrain-store") declare internalSupergrainStore: InternalSupergrainStore

  private readonly optimisticQueues = new Map<string, Queue<OptimisticPatchData>>()
  private isReceiving = true

  /**
   * Clears both internal stores.
   */
  clearMemory(): void {
    this.internalEmberStore.reset()
    this.internalSupergrainStore.documentStore.clearMemory()
  }

  applyLocalPatch(
    patch: PatchModel,
    useServerReconciliation = this.serverReconciliationEnabled
  ): boolean {
    const type = patch.document.modelType as keyof TypeToModel
    if (!this.hasDocument(type, patch.document.id) && patch.isCreatePatch !== true) return false

    if (useServerReconciliation) {
      this.applyOptimisticPatch(patch)
    } else {
      this.patchBackends(patch)
    }

    this.trigger("patch", patch)
    return true
  }

  applyLocalActionPatches(actionDoc: ActionDoc): void {
    const patches = actionDoc.attributes.patches as Array<PatchModel>
    if (patches.length === 0) return

    begin()
    try {
      patches.forEach((patch) => {
        if (patch.document.id === null || patch.document.id === undefined) {
          console.log("Problematic Patch", patch)
          throw new Error("Invalid Patch -- no id")
        }

        if (this.applyLocalPatch(patch)) {
          const type = patch.document.modelType as keyof TypeToModel
          patch.revisionAtCreation = this.getPatchRevision(type, patch.document.id)
        }
      })
    } finally {
      end()
    }
  }

  applyForeignPatch(patch: PatchModel) {
    this.safeRemotePatchBackends(patch)
    this.trigger("foreign-patch", patch)
  }

  private applyServerPatchWithReconciliation(
    patch: PatchModel,
    id: string,
    type: ServerPatchResourceType
  ): PatchModel | null {
    const queue = this.optimisticQueues.get(modelKey(patch))
    const oldestOptimistic = queue?.head()
    if (queue && oldestOptimistic && oldestOptimistic.patch.id === id) {
      queue.shift()
      return null
    }

    const optimisticPatchData = queue?.values() ?? []
    queue?.clear()
    for (let i = optimisticPatchData.length - 1; i >= 0; i--) {
      optimisticPatchData[i].rewind()
    }

    if (type === "patch-summary") {
      console.error("Server echoed our patch-summary out of order", id, modelKey(patch))
      const optimisticPatch = optimisticPatchData.find((data) => data.patch.id === id)?.patch
      if (optimisticPatch === undefined) {
        console.error("  Local patch for patch-summary not found. Duplicate ack from server?")
        return null
      }
      patch = optimisticPatch
    }

    this.safeRemotePatchBackends(patch)

    for (let i = 0; i < optimisticPatchData.length; i++) {
      if (optimisticPatchData[i].patch.id === id) {
        console.error(
          "Server echoed our patch out of order",
          id,
          modelKey(patch),
          `${i}/${optimisticPatchData.length}`
        )
      } else {
        this.applyOptimisticPatch(optimisticPatchData[i].patch)
      }
    }

    if (type === "patch-summary") return null

    this.trigger("foreign-patch", patch)
    return patch
  }

  acceptServerPatch(serverPatch: ServerPatchResource): PatchModel | null {
    const patch = patchModelFromServerPatch(serverPatch)
    let eventPatch: PatchModel | null = null
    if (this.serverReconciliationEnabled) {
      eventPatch = this.applyServerPatchWithReconciliation(patch, serverPatch.id, serverPatch.type)
    } else if (serverPatch.type !== "patch-summary") {
      this.applyForeignPatch(patch)
      eventPatch = patch
    }

    this.updateServerRevisionMetadata(patch)
    return eventPatch
  }

  receiveServerPatch(serverPatch: ServerPatchResource): ServerPatchReceiveResult {
    if (!this.isReceiving) {
      return { kind: "ignored" }
    }

    const patch = patchModelFromServerPatch(serverPatch)
    const modelType = patch.document.modelType as keyof TypeToModel
    const modelId = patch.document.id
    const revisionAfterPatch = patch.revisionAfterServerApplication

    if (!this.hasDocument(modelType, modelId) || revisionAfterPatch === undefined) {
      return { kind: "ignored" }
    }

    const currentRevision = this.getRevision(modelType, modelId)

    if (currentRevision + 1 === revisionAfterPatch) {
      return { kind: "accepted", patch: this.acceptServerPatch(serverPatch) }
    }

    if (currentRevision >= revisionAfterPatch) {
      return { kind: "ignored" }
    }

    // Phantom CardStacks start at revision 0 and do not exist on the server yet.
    if (currentRevision === 0 && modelType === "card-stack") {
      return { kind: "ignored" }
    }

    return { kind: "request-revision", modelType, modelId, revision: currentRevision }
  }

  private patchSupergrain(patch: PatchModel): (() => void) | null {
    return this.internalSupergrainStore.patch(patch)
  }

  hasDocument(type: keyof TypeToModel, id: string): boolean {
    return (
      this.internalSupergrainStore.hasDocument(type, id) ||
      this.internalEmberStore.hasDocument(type, id)
    )
  }

  getRevision(type: keyof TypeToModel, id: string): number {
    if (this.internalSupergrainStore.hasDocument(type, id)) {
      return this.internalSupergrainStore.getRevision(type, id)
    }
    return this.internalEmberStore.getRevision(type, id)
  }

  clearQueue(type: keyof TypeToModel, id: string): void {
    this.optimisticQueues.get(`${type}.${id}`)?.clear()
  }

  /**
   * Debug helper to disable receiving server patches.
   *
   * Note: We don't automatically request the missed patches when this is set back to true.
   */
  setIsReceiving(isReceiving: boolean): void {
    this.isReceiving = isReceiving
  }

  private get serverReconciliationEnabled(): boolean {
    return !!this.session.user?.content?.attributes?.featureFlags["SERVER_RECONCILIATION"]
  }

  private safeRemotePatchBackends(patch: PatchModel): void {
    try {
      this.patchBackends(patch)
    } catch (error) {
      // The patch was not valid for our state, e.g., because it is for a field
      // we haven't loaded or an array entry that was just deleted locally.
      // Track in Sentry, but let the caller continue.
      console.error("Remote patch failed, skipping", error)
      Sentry.withScope((scope) => {
        scope.setLevel(Sentry.Severity.Warning)
        scope.setExtra("error", error)
        scope.setExtra("patch", JSON.stringify(patch))
        Sentry.captureMessage("Remote patch failed")
      })
    }
  }

  private patchBackends(patch: PatchModel): void {
    this.internalEmberStore.patch(patch)
    this.patchSupergrain(patch)
  }

  private applyOptimisticPatch(patch: PatchModel): void {
    const rewindEmber = this.internalEmberStore.patch(patch)
    const rewindSupergrain = this.patchSupergrain(patch)

    const rewind = () => {
      rewindEmber?.()
      rewindSupergrain?.()
    }

    let queue = this.optimisticQueues.get(modelKey(patch))
    if (queue === undefined) {
      queue = new Queue()
      this.optimisticQueues.set(modelKey(patch), queue)
    }
    queue.push({ patch, rewind })
  }

  private getPatchRevision(type: keyof TypeToModel, id: string): string | number | undefined {
    const emberModel = this.internalEmberStore.findInMemory(type, id)
    const emberRevision = revisionFromModel(emberModel)
    if (emberRevision !== null && emberRevision !== undefined) return emberRevision

    const supergrainModel = this.internalSupergrainStore.findInMemory(type, id)
    const supergrainRevision = revisionFromModel(supergrainModel)
    if (supergrainRevision !== null && supergrainRevision !== undefined) return supergrainRevision

    return undefined
  }

  private updateServerRevisionMetadata(patch: ServerPatchModel): void {
    const revision = patch.revisionAfterServerApplication
    // Only our own patches' summaries carry it: other sessions' patches increment it in their ops.
    const version = patch.versionAfterServerApplication
    const type = patch.document.modelType as keyof TypeToModel
    const id = patch.document.id

    const currentModel = this.internalEmberStore.findInMemory(type, id)
    if (currentModel) {
      set(currentModel, "attributes._revision", revision)
      if (typeof version === "number") set(currentModel, "attributes.version", version)
    }

    const supergrainModel = this.internalSupergrainStore.findInMemory(type, id)
    if (supergrainModel) {
      setRevisionMetadata(supergrainModel, revision, version)
    }
  }
}

function modelKey(patch: PatchModel): string {
  return `${patch.document.modelType}.${patch.document.id}`
}

function patchModelFromServerPatch(serverPatch: ServerPatchResource): ServerPatchModel {
  const { attributes } = serverPatch

  return {
    ...attributes,
    id: serverPatch.id,
    document: {
      ...attributes.document,
      ops: attributes.document.ops ?? {},
    },
    embeddedDocument: attributes.embeddedDocument ?? null,
  }
}

function revisionFromModel(model: unknown): string | number | undefined {
  if (typeof model !== "object" || model === null || !("attributes" in model)) return undefined

  const { attributes } = model
  if (typeof attributes !== "object" || attributes === null || !("_revision" in attributes)) {
    return undefined
  }

  const { _revision } = attributes
  if (typeof _revision === "string" || typeof _revision === "number") return _revision

  return undefined
}

function setRevisionMetadata(
  model: unknown,
  revision: number | undefined,
  version: number | null | undefined
): void {
  if (!isRecord(model)) return

  const attributes = getOrCreateRecord(model, "attributes")
  attributes._revision = revision
  if (typeof version === "number") attributes.version = version

  getOrCreateRecord(model, "meta")
}

function getOrCreateRecord(record: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = record[key]
  if (isRecord(value)) return value

  // Written into a stored Supergrain document, which must stay prototype-free
  // at every level (see ../lib/strip-prototypes.ts).
  const next: Record<string, unknown> = Object.create(null)
  record[key] = next
  return next
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

declare module "@ember/service" {
  interface Registry {
    "patch-coordinator": PatchCoordinator
  }
}
